import {
  Asset,
  Token,
  TokenId,
  Vault,
  addressEqual,
  getTotalTokenIds,
} from 'nftx.js';

const isDefined = <T>(x: T | undefined): x is T => x != null;

const getTotalHoldings = ({
  mintableAssets,
  vault,
}: {
  mintableAssets: Asset[];
  vault: Pick<Vault, 'asset'>;
}) => {
  const collectionAssets = mintableAssets.filter((asset) =>
    addressEqual(asset.assetAddress, vault.asset.id)
  );
  return getTotalTokenIds(
    collectionAssets.map(
      (asset) =>
        [asset.tokenId, Number(asset.quantity ?? 1n)] as [TokenId, number]
    )
  );
};

const createCollection = ({
  collections,
  mintableAssets,
  vault,
}: {
  mintableAssets: Asset[];
  vault: Pick<Vault, 'asset'>;
  collections: Record<
    string,
    Token & { vaultIds: string[]; totalHoldings: number }
  >;
}) => {
  const totalHoldings = getTotalHoldings({ mintableAssets, vault });

  const collection = {
    ...vault.asset,
    vaultIds: [],
    totalHoldings,
  };

  collections[vault.asset.id] = collection;

  return collection;
};

const getCollection = ({
  collections,
  mintableAssets,
  vault,
}: {
  collections: Record<
    string,
    Token & { vaultIds: string[]; totalHoldings: number }
  >;
  vault: Pick<Vault, 'asset'>;
  mintableAssets: Asset[];
}) => {
  const collection = collections[vault.asset.id];
  if (!collection) {
    return createCollection({ mintableAssets, vault, collections });
  }
  return collection;
};

const getMintableCollections = ({
  mintableAssets,
  vaults,
}: {
  mintableAssets: Asset[];
  vaults: Pick<Vault, 'vaultId' | 'asset'>[] | undefined;
}) => {
  if (!mintableAssets.length || !vaults) {
    return [];
  }
  // mintableAssets is already filtered down based on all the same criteria as mintableCollections
  // so we can just use the resulting list to build our vaultIds array
  const allVaultIds = mintableAssets
    .flatMap((x) => x.vaultIds)
    .filter(isDefined);
  const vaultIds = [...new Set(allVaultIds)];
  const collections: Record<
    string,
    Token & { vaultIds: string[]; totalHoldings: number }
  > = {};

  vaultIds.forEach((vaultId) => {
    const vault = vaults.find((v) => v.vaultId === vaultId);
    if (!vault) {
      return;
    }
    const collection = getCollection({ collections, vault, mintableAssets });

    collection.vaultIds.push(vaultId);
  });

  return Object.values(collections);
};

export default getMintableCollections;
